import React, { useMemo, useState, useRef, useEffect } from "react";
import { DynamicIcon } from "lucide-react/dynamic";
import { useTwitterFilters } from "@/hooks/twitter/useTwitterFilters";
import type {
  TwitterFiltersState,
  TwitterTimeframeOption,
} from "@/types/mediaInsights/twitter";

// Add styles for vertical range slider
const sliderStyles = `
  input[type="range"][style*="vertical"] {
    writing-mode: vertical-lr;
    direction: rtl;
    -webkit-appearance: slider-vertical;
  }
  
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  input[type="range"]::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  input[type="range"]:hover::-webkit-slider-thumb {
    background: #2563eb;
  }
  
  input[type="range"]:hover::-moz-range-thumb {
    background: #2563eb;
  }
`;

// Inject styles into document head
if (typeof document !== "undefined") {
  const styleId = "twitter-filters-slider-styles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = sliderStyles;
    document.head.appendChild(style);
  }
}

interface TwitterFiltersProps {
  filters: TwitterFiltersState;
  onFiltersChange: (filters: Partial<TwitterFiltersState>) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const DEFAULT_TICKERS = [
  "BTC",
  "ETH",
  "SOL",
  "AVAX",
  "DOGE",
  "MATIC",
  "ARB",
  "OP",
  "LINK",
  "AAVE",
];

const DEFAULT_AUTHORS = [
  "coinbase",
  "binance",
  "coindesk",
  "theblock__",
  "blockchain",
  "a16zcrypto",
];

const TIMEFRAME_OPTIONS: {
  id: TwitterTimeframeOption;
  label: string;
  description: string;
}[] = [
  {
    id: "all",
    label: "All time",
    description: "Show the full dataset",
  },
  {
    id: "24h",
    label: "Last 24 hours",
    description: "Tweets posted within the past day",
  },
  {
    id: "7d",
    label: "Last 7 days",
    description: "Weekly activity snapshot",
  },
  {
    id: "30d",
    label: "Last 30 days",
    description: "Expanded lookback window",
  },
  {
    id: "custom",
    label: "Custom range",
    description: "Set a specific start and end time",
  },
];

const MAX_VISIBLE_TICKERS = 8;
const MAX_VISIBLE_AUTHORS = 6;
const MIN_ALPHA_GRADE = 0;
const MAX_ALPHA_GRADE = 100;

const TwitterFilters: React.FC<TwitterFiltersProps> = ({
  filters,
  onFiltersChange,
  isOpen,
  onToggle,
}) => {
  const [isTickerExpanded, setIsTickerExpanded] = useState(false);
  const [isAuthorsExpanded, setIsAuthorsExpanded] = useState(false);
  const [tickerSearch, setTickerSearch] = useState("");
  const [authorSearch, setAuthorSearch] = useState("");
  const filterRef = useRef<HTMLDivElement>(null);

  // Fetch available filters from the backend
  const { tickers: availableTickers, authors: availableAuthors } =
    useTwitterFilters();

  // Handle clicking outside the filter component and reset search when modal opens
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Reset search inputs when modal opens
      setTickerSearch("");
      setAuthorSearch("");
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const tickerOptions = useMemo(() => {
    const source =
      availableTickers.length > 0 ? availableTickers : DEFAULT_TICKERS;
    const allTickers = Array.from(
      new Set(source.map((ticker: string) => ticker.trim()))
    )
      .filter(Boolean)
      .sort((a: string, b: string) => a.localeCompare(b));

    if (!tickerSearch.trim()) {
      return allTickers;
    }

    return allTickers.filter((ticker: string) =>
      ticker.toLowerCase().includes(tickerSearch.toLowerCase())
    );
  }, [availableTickers, tickerSearch]);

  const authorOptions = useMemo(() => {
    const source =
      availableAuthors.length > 0 ? availableAuthors : DEFAULT_AUTHORS;
    const allAuthors = Array.from(
      new Set(source.map((author: string) => author.trim()))
    )
      .filter(Boolean)
      .sort((a: string, b: string) => a.localeCompare(b));

    if (!authorSearch.trim()) {
      return allAuthors;
    }

    return allAuthors.filter((author: string) =>
      author.toLowerCase().includes(authorSearch.toLowerCase())
    );
  }, [availableAuthors, authorSearch]);

  const handleCollectionChange = (
    key: "tickers" | "authors",
    value: string,
    checked: boolean
  ) => {
    const current = filters[key];
    const next = checked
      ? [...current, value]
      : current.filter((entry) => entry !== value);
    onFiltersChange({ [key]: next });
  };

  const handleTimeframeChange = (value: TwitterTimeframeOption) => {
    if (value === "custom") {
      onFiltersChange({ timeframe: value });
      return;
    }

    onFiltersChange({
      timeframe: value,
      startDate: null,
      endDate: null,
    });
  };

  const clearAllFilters = () => {
    const clearedFilters: Partial<TwitterFiltersState> = {
      tickers: [],
      authors: [],
      timeframe: "all",
      startDate: null,
      endDate: null,
    };
    // Remove alphaGradeRange by not including it
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters =
    filters.tickers.length > 0 ||
    filters.authors.length > 0 ||
    filters.timeframe !== "all" ||
    (filters.alphaGradeRange !== undefined &&
      (filters.alphaGradeRange[0] !== MIN_ALPHA_GRADE ||
        filters.alphaGradeRange[1] !== MAX_ALPHA_GRADE));

  const activeFiltersCount =
    filters.tickers.length +
    filters.authors.length +
    (filters.timeframe !== "all" ? 1 : 0) +
    (filters.alphaGradeRange !== undefined &&
    (filters.alphaGradeRange[0] !== MIN_ALPHA_GRADE ||
      filters.alphaGradeRange[1] !== MAX_ALPHA_GRADE)
      ? 1
      : 0);

  return (
    <div className="relative" ref={filterRef}>
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 ${
          isOpen || hasActiveFilters
            ? "bg-blue-600 hover:bg-blue-500 border-blue-500 text-white shadow-lg shadow-blue-500/20"
            : "bg-gray-800 hover:bg-gray-700 border-gray-600 text-white"
        }`}
      >
        <DynamicIcon name="filter" className="w-4 h-4" />
        Advanced Filters
        {hasActiveFilters && (
          <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-white/20 border border-white/30">
            {activeFiltersCount}
          </span>
        )}
        <DynamicIcon
          name={isOpen ? "chevron-up" : "chevron-down"}
          className="w-4 h-4"
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-[80rem] max-w-[95vw] bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
            <h3 className="text-lg font-semibold text-white">Filters</h3>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="flex gap-6 px-6 pb-6 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
            <div className="grid grid-cols-3 gap-6 flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Time range
                </label>
                <div className="space-y-2">
                  {TIMEFRAME_OPTIONS.map((option) => (
                    <label
                      key={option.id}
                      className="flex items-center justify-between gap-3 cursor-pointer rounded-lg border border-gray-700 hover:border-gray-600 px-3 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="timeframe"
                          checked={filters.timeframe === option.id}
                          onChange={() => handleTimeframeChange(option.id)}
                          className="w-4 h-4 text-blue-600 border-gray-600 focus:ring-blue-500 bg-gray-700"
                        />
                        <div>
                          <p className="text-sm text-gray-200 font-medium">
                            {option.label}
                          </p>
                          <p className="text-xs text-gray-400">
                            {option.description}
                          </p>
                        </div>
                      </div>
                      <DynamicIcon
                        name="clock"
                        className="w-4 h-4 text-blue-400"
                      />
                    </label>
                  ))}
                </div>
                {filters.timeframe === "custom" && (
                  <div className="mt-3 grid grid-cols-1 gap-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-400">Start</span>
                      <input
                        type="datetime-local"
                        value={filters.startDate ?? ""}
                        onChange={(e) =>
                          onFiltersChange({ startDate: e.target.value })
                        }
                        className="bg-gray-900 border border-gray-700 rounded-md px-2 py-1 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-400">End</span>
                      <input
                        type="datetime-local"
                        value={filters.endDate ?? ""}
                        onChange={(e) =>
                          onFiltersChange({ endDate: e.target.value })
                        }
                        className="bg-gray-900 border border-gray-700 rounded-md px-2 py-1 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-300">
                    Authors
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search authors..."
                      value={authorSearch}
                      onChange={(e) => setAuthorSearch(e.target.value)}
                      className="bg-gray-900 border border-gray-700 rounded-md px-3 py-1 text-sm text-gray-200 focus:border-blue-500 focus:outline-none w-40"
                    />
                    <DynamicIcon
                      name="search"
                      className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  {authorOptions
                    .slice(
                      0,
                      isAuthorsExpanded
                        ? authorOptions.length
                        : MAX_VISIBLE_AUTHORS
                    )
                    .map((author) => (
                      <label
                        key={author}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.authors.includes(author)}
                          onChange={(e) =>
                            handleCollectionChange(
                              "authors",
                              author,
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 border-gray-600 rounded focus:ring-blue-500 bg-gray-700"
                        />
                        <span className="text-sm text-gray-300">@{author}</span>
                      </label>
                    ))}
                </div>
                {authorOptions.length > MAX_VISIBLE_AUTHORS && (
                  <div className="flex justify-center mt-3">
                    <button
                      onClick={() => setIsAuthorsExpanded(!isAuthorsExpanded)}
                      className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {isAuthorsExpanded ? (
                        <>
                          Show less
                          <DynamicIcon name="chevron-up" className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          Show more (
                          {authorOptions.length - MAX_VISIBLE_AUTHORS} more)
                          <DynamicIcon
                            name="chevron-down"
                            className="w-4 h-4"
                          />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-300">
                    Ticker tags
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search tickers..."
                      value={tickerSearch}
                      onChange={(e) => setTickerSearch(e.target.value)}
                      className="bg-gray-900 border border-gray-700 rounded-md px-3 py-1 text-sm text-gray-200 focus:border-blue-500 focus:outline-none w-40"
                    />
                    <DynamicIcon
                      name="search"
                      className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {tickerOptions
                    .slice(
                      0,
                      isTickerExpanded
                        ? tickerOptions.length
                        : MAX_VISIBLE_TICKERS
                    )
                    .map((ticker) => (
                      <label
                        key={ticker}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.tickers.includes(ticker)}
                          onChange={(e) =>
                            handleCollectionChange(
                              "tickers",
                              ticker,
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-blue-600 border-gray-600 rounded focus:ring-blue-500 bg-gray-700"
                        />
                        <span className="text-sm text-gray-300">${ticker}</span>
                      </label>
                    ))}
                </div>
                {tickerOptions.length > MAX_VISIBLE_TICKERS && (
                  <div className="flex justify-center mt-3">
                    <button
                      onClick={() => setIsTickerExpanded(!isTickerExpanded)}
                      className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {isTickerExpanded ? (
                        <>
                          Show less
                          <DynamicIcon name="chevron-up" className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          Show more (
                          {tickerOptions.length - MAX_VISIBLE_TICKERS} more)
                          <DynamicIcon
                            name="chevron-down"
                            className="w-4 h-4"
                          />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Alpha Grade Slider */}
            <div className="flex flex-col items-center px-4 pb-6 border-l border-gray-700">
              <label className="text-sm font-medium text-gray-300 mb-4 whitespace-nowrap">
                Alpha Grade
              </label>
              <div className="flex flex-col items-center flex-1 relative">
                {/* Max value label */}
                <div className="text-xs text-gray-400 mb-2 font-mono">
                  {filters.alphaGradeRange?.[1] ?? MAX_ALPHA_GRADE}
                </div>

                {/* Vertical slider track */}
                <div className="relative h-64 w-2 bg-gray-700 rounded-full">
                  {/* Filled track between sliders */}
                  <div
                    className="absolute w-full bg-blue-500 rounded-full"
                    style={{
                      bottom: `${
                        ((filters.alphaGradeRange?.[0] ?? MIN_ALPHA_GRADE) /
                          MAX_ALPHA_GRADE) *
                        100
                      }%`,
                      height: `${
                        (((filters.alphaGradeRange?.[1] ?? MAX_ALPHA_GRADE) -
                          (filters.alphaGradeRange?.[0] ?? MIN_ALPHA_GRADE)) /
                          MAX_ALPHA_GRADE) *
                        100
                      }%`,
                    }}
                  />

                  {/* Max thumb */}
                  <input
                    type="range"
                    min={MIN_ALPHA_GRADE}
                    max={MAX_ALPHA_GRADE}
                    value={filters.alphaGradeRange?.[1] ?? MAX_ALPHA_GRADE}
                    onChange={(e) => {
                      const maxVal = parseInt(e.target.value);
                      const minVal =
                        filters.alphaGradeRange?.[0] ?? MIN_ALPHA_GRADE;
                      onFiltersChange({
                        alphaGradeRange: [Math.min(minVal, maxVal), maxVal],
                      });
                    }}
                    className="absolute left-1/2 -translate-x-1/2 w-64 h-2 appearance-none bg-transparent cursor-pointer [writing-mode:vertical-lr] [direction:rtl]"
                    style={{
                      WebkitAppearance: "slider-vertical",
                    }}
                  />

                  {/* Min thumb */}
                  <input
                    type="range"
                    min={MIN_ALPHA_GRADE}
                    max={MAX_ALPHA_GRADE}
                    value={filters.alphaGradeRange?.[0] ?? MIN_ALPHA_GRADE}
                    onChange={(e) => {
                      const minVal = parseInt(e.target.value);
                      const maxVal =
                        filters.alphaGradeRange?.[1] ?? MAX_ALPHA_GRADE;
                      onFiltersChange({
                        alphaGradeRange: [minVal, Math.max(minVal, maxVal)],
                      });
                    }}
                    className="absolute left-1/2 -translate-x-1/2 w-64 h-2 appearance-none bg-transparent cursor-pointer [writing-mode:vertical-lr] [direction:rtl]"
                    style={{
                      WebkitAppearance: "slider-vertical",
                    }}
                  />
                </div>

                {/* Min value label */}
                <div className="text-xs text-gray-400 mt-2 font-mono">
                  {filters.alphaGradeRange?.[0] ?? MIN_ALPHA_GRADE}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwitterFilters;
